import React, { useLayoutEffect, useRef } from "react";
import {
  Table as TableIcon,
  Video,
  Image as ImageIcon,
  ChevronDown,
  GripVertical,
  Trash2,
  Type,
  Code,
  MousePointerClick,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import RichTextEditor from "./RichTextEditor";
import TableEditor from "./TableEditor";
import MediaManager from "./MediaManager";
import { PropertySelector } from "./PropertySelector";
import { BlogSelector } from "./BlogSelector";

const SortableBlock = ({ block, onUpdate, onDelete, read }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : 0,
    opacity: isDragging ? 0.6 : 1,
  };

  const textareaRef = useRef(null);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [block.html]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group/block relative flex flex-col rounded-xl border border-slate-200/80 bg-white transition-all ${
        isDragging ? "shadow-2xl z-50 ring-2 ring-indigo-500/20" : "shadow-2xs"
      }`}
    >
      {!read && (
        /* Universal Top Actions & Drag Handle Bar */
        <div className="flex items-center justify-between bg-slate-50/90 border-b border-slate-200/80 px-3 py-2 select-none">
          <div
            {...attributes}
            {...listeners}
            className="flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-primary cursor-grab active:cursor-grabbing py-0.5 px-1.5 -ml-1.5 rounded-lg hover:bg-slate-200/60 transition-all"
            title="Drag to reorder block"
          >
            <GripVertical className="w-4 h-4 text-slate-400 group-hover/block:text-indigo-500 transition-colors" />
            <span className="capitalize">
              {block.type.replace("-", " ")} Block
            </span>
          </div>

          <button
            type="button"
            onClick={onDelete}
            className="p-1 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
            title="Delete Block"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Main Block Content */}
      <div className="flex-1 min-w-0 w-full p-3 sm:p-4">
        {block.type === "rich-text" && (
          <RichTextEditor
            value={block.content || ""}
            onChange={(val) => onUpdate({ content: val })}
            readOnly={read}
          />
        )}
        {block.type === "table" && (
          <TableEditor
            config={block.table}
            onUpdate={(u) => onUpdate({ table: { ...block.table, ...u } })}
            onDelete={onDelete}
            readOnly={read}
          />
        )}
        {block.type === "media" && (
          <MediaManager
            asset={block.media}
            onUpdate={(u) => onUpdate({ media: { ...block.media, ...u } })}
            onDelete={onDelete}
            readOnly={read}
          />
        )}
        {block.type === "property-selector" && (
          <PropertySelector
            value={block.values || []}
            onChange={(val) => onUpdate({ values: val })}
            readOnly={read}
          />
        )}
        {block.type === "blog-selector" && (
          <BlogSelector
            value={block.values || []}
            onChange={(val) => onUpdate({ values: val })}
            readOnly={read}
          />
        )}
        {block.type === "custom-html" && (
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 block">
              Custom HTML
            </label>
            <textarea
              ref={textareaRef}
              disabled={read}
              value={block.html || ""}
              onChange={(e) => onUpdate({ html: e.target.value })}
              placeholder="<div>Your custom HTML here...</div>"
              className="w-full min-h-[140px] sm:min-h-[180px] p-3 text-xs sm:text-sm font-mono border border-slate-200 rounded-lg focus:outline-hidden focus:border-primary focus:ring-4 focus:ring-primary/10 disabled:bg-slate-50 text-slate-800 resize-none overflow-hidden"
            />
          </div>
        )}

        {block.type === "cta" && (
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              Call to Action Settings
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  Button Text
                </label>
                <input
                  disabled={read}
                  type="text"
                  value={block.cta?.label || ""}
                  onChange={(e) =>
                    onUpdate({
                      cta: { ...block.cta, label: e.target.value },
                    })
                  }
                  maxLength={25}
                  placeholder="e.g. Buy Now"
                  className="w-full px-3 py-2 text-xs sm:text-sm border border-slate-200 rounded-lg focus:outline-hidden focus:border-primary focus:ring-4 focus:ring-primary/10 disabled:bg-slate-50 transition-all"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  Destination Link
                </label>
                <input
                  disabled={read}
                  type="url"
                  value={block.cta?.link || ""}
                  onChange={(e) =>
                    onUpdate({
                      cta: { ...block.cta, link: e.target.value },
                    })
                  }
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 text-xs sm:text-sm border border-slate-200 rounded-lg focus:outline-hidden focus:border-primary focus:ring-4 focus:ring-primary/10 disabled:bg-slate-50 transition-all"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                disabled={read}
                id={`newTab-${block.id}`}
                type="checkbox"
                checked={block.cta?.newTab || false}
                onChange={(e) =>
                  onUpdate({
                    cta: { ...block.cta, newTab: e.target.checked },
                  })
                }
                className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
              />
              <label
                htmlFor={`newTab-${block.id}`}
                className="text-xs font-medium text-slate-600 cursor-pointer select-none"
              >
                Open link in a new tab
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ContentSectionEditor = ({ section, onUpdate, read = false }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const addBlock = (type, subtype) => {
    let newBlock = { id: crypto.randomUUID(), type };
    if (type === "rich-text") newBlock.content = "";
    if (type === "custom-html") newBlock.html = "";
    if (type === "table") {
      newBlock.table = {
        rows: 2,
        cols: 2,
        hasHeaderRow: true,
        hasHeaderCol: false,
        hasFooterRow: false,
        stylePreset: "bordered",
        mobileBehavior: "scroll",
        data: Array(2)
          .fill(null)
          .map(() =>
            Array(2)
              .fill(null)
              .map(() => ({
                content: "",
                subtext: "",
                isHeader: false,
              })),
          ),
      };
    }
    if (type === "media") {
      newBlock.media = {
        id: crypto.randomUUID(),
        type: subtype || "image",
        url: "",
        altText: subtype === "image" ? "" : undefined,
        duration: 0,
        description: "",
      };
    }
    if (type === "cta") {
      newBlock.cta = {
        label: "",
        link: "",
        newTab: true,
      };
    }
    onUpdate({ blocks: [...section.blocks, newBlock] });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = section.blocks.findIndex((i) => i.id === active.id);
      const newIndex = section.blocks.findIndex((i) => i.id === over.id);
      onUpdate({ blocks: arrayMove(section.blocks, oldIndex, newIndex) });
    }
  };

  return (
    <section
      className={`bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-xs hover:shadow-sm transition-all ${
        read ? "pointer-events-none opacity-90" : ""
      }`}
    >
      {/* Header Section */}
      <div className="border-b border-slate-200/80 p-4 sm:p-6 bg-slate-50/60">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          {!read && (
            <div className="relative shrink-0 w-24 sm:w-auto">
              <select
                value={section.headingTag}
                onChange={(e) => onUpdate({ headingTag: e.target.value })}
                className="w-full appearance-none bg-white border border-slate-200/90 pl-3 pr-8 py-1.5 rounded-xl text-xs sm:text-sm font-bold text-slate-700 focus:outline-hidden focus:border-primary shadow-2xs transition-all cursor-pointer"
              >
                <option value="h1">H1</option>
                <option value="h2">H2</option>
                <option value="h3">H3</option>
                <option value="h4">H4</option>
                <option value="h5">H5</option>
              </select>
              <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          )}
          <input
            disabled={read}
            type="text"
            placeholder="Section Heading..."
            className="w-full bg-transparent border-none text-lg sm:text-2xl font-bold text-slate-900 placeholder:text-slate-300 focus:ring-0 p-0"
            value={section.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
          />
        </div>

        {/* Dynamic Add-Block Options */}
        {!read && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={() => addBlock("rich-text")}
              className="flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200/80 rounded-xl hover:border-primary hover:text-primary shadow-2xs transition-all active:scale-95 cursor-pointer"
            >
              <Type className="w-3.5 h-3.5 shrink-0" /> Text
            </button>
            <button
              type="button"
              onClick={() => addBlock("table")}
              className="flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200/80 rounded-xl hover:border-primary hover:text-primary shadow-2xs transition-all active:scale-95 cursor-pointer"
            >
              <TableIcon className="w-3.5 h-3.5 shrink-0" /> Table
            </button>
            <button
              type="button"
              onClick={() => addBlock("media", "image")}
              className="flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200/80 rounded-xl hover:border-primary hover:text-primary shadow-2xs transition-all active:scale-95 cursor-pointer"
            >
              <ImageIcon className="w-3.5 h-3.5 shrink-0" /> Image
            </button>
            <button
              type="button"
              onClick={() => addBlock("media", "video")}
              className="flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200/80 rounded-xl hover:border-primary hover:text-primary shadow-2xs transition-all active:scale-95 cursor-pointer"
            >
              <Video className="w-3.5 h-3.5 shrink-0" /> Video
            </button>
            <button
              type="button"
              onClick={() => addBlock("custom-html")}
              className="flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200/80 rounded-xl hover:border-primary hover:text-primary shadow-2xs transition-all active:scale-95 cursor-pointer"
            >
              <Code className="w-3.5 h-3.5 shrink-0" /> Custom HTML
            </button>
            <button
              type="button"
              onClick={() => addBlock("cta")}
              className="flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200/80 rounded-xl hover:border-primary hover:text-primary shadow-2xs transition-all active:scale-95 cursor-pointer"
            >
              <MousePointerClick className="w-3.5 h-3.5 shrink-0" /> CTA Button
            </button>
          </div>
        )}
      </div>

      {/* Content Blocks Area */}
      <div className="p-3 sm:p-6 lg:p-8">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={section.blocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-6 sm:space-y-8">
              {section.blocks.map((block) => (
                <SortableBlock
                  key={block.id}
                  block={block}
                  read={read}
                  onUpdate={(u) => {
                    const idx = section.blocks.findIndex(
                      (b) => b.id === block.id,
                    );
                    const newBlocks = [...section.blocks];
                    newBlocks[idx] = {
                      ...newBlocks[idx],
                      ...u,
                    };
                    onUpdate({ blocks: newBlocks });
                  }}
                  onDelete={() =>
                    onUpdate({
                      blocks: section.blocks.filter((b) => b.id !== block.id),
                    })
                  }
                />
              ))}

              {section.blocks.length === 0 && (
                <div className="py-8 sm:py-12 border-2 border-dashed border-slate-200 rounded-2xl text-center text-slate-400 text-xs sm:text-sm italic">
                  No content blocks yet. Add one from the options above.
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </section>
  );
};

export default ContentSectionEditor;
